/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

const libEntityOptionSource = require('./_EntityOptionSource.js');

class CustomInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		/** @type {Record<string, {Loaded: boolean, Options: Array<{id: any, text: string}>, Callbacks: Array<Function>}>} Shared entity-backed option sets, keyed by entity + field config. */
		this._entitySelectOptionCache = {};
		if (window['jQuery'] && window['jQuery'].fn.select2)
		{
			window['jQuery'].fn.select2.amd.define('select2/data/dynamicLocalPaginationAdapter',
				[
					'select2/data/array',
					'select2/utils',
				],
				function(ArrayAdapter, Utils)
				{
					function DynamicLocalPaginationAdapter($element, options)
					{
						// @ts-ignore — __super__ is injected by Select2's Utils.Extend
						DynamicLocalPaginationAdapter.__super__.constructor.call(this, $element, options);
					}
					Utils.Extend(DynamicLocalPaginationAdapter, ArrayAdapter);

					DynamicLocalPaginationAdapter.prototype.query = function(params, callback)
					{
						//TODO: customizable
						const pageSize = 100;

						if (!('page' in params))
						{
							params.page = 1;
						}
						const term = (params.term || '').toLowerCase();
						/** @type {any} */
						const tmpSelf = this;
						const results = tmpSelf.options.options.DynamicMetaLists.getList(tmpSelf.options.options.Input.PictForm.SelectOptionsPickList,
							{
								startIndex: (params.page - 1) * pageSize,
								maxResults: pageSize,
								filterString: term,
								searchFields: (tmpSelf.options.options.Input.PictForm.DistillingDisplay || '').split(',').filter((f) => f.trim() !== ''),
							});
						callback(
						{
							results,
							pagination:
							{
								more: results.length >= pageSize,
							},
						});
					};

					return DynamicLocalPaginationAdapter;
				});
		}
	}

	/**
	 * Temporary loading skeleton for the input element.
	 * @param {object} inputID The input id.
	 */
	addLoadingSkeleton(inputID, rawID)
	{
		if (!this.pict?.AppData?.Interactive || this.pict?.AppData?.DocumentData?.ReportData?.Locked)
		{
			return;
		}
		const tmpLoadingBox = document.createElement('div');
		tmpLoadingBox.id = `LOADING-${rawID}`;
		tmpLoadingBox.classList.add('input', 'is-skeleton');
		tmpLoadingBox.style.left = '0';
		tmpLoadingBox.style.position = 'absolute';
		tmpLoadingBox.style.opacity = '0.5';
		this.pict.ContentAssignment.getElement(inputID)[0]?.parentElement?.appendChild(tmpLoadingBox);
		this.pict.ContentAssignment.addClass(inputID, 'is-hidden');
	}

	/**
	 * Remove the loading skeleton from the input element.
	 * @param {object} inputID The input id.
	 */
	removeLoadingSkeleton(inputID, rawID)
	{
		const tmpLoadingBox = this.pict.ContentAssignment.getElement(`#LOADING-${rawID}`);
		if (tmpLoadingBox && tmpLoadingBox.length > 0)
		{
			tmpLoadingBox[0].remove();
		}
		this.pict.ContentAssignment.removeClass(inputID, 'is-hidden');
	}

	/**
	 * Generates the HTML ID for a select input element.
	 *
	 * @param {string} pInputHTMLID - The HTML ID of the input element.
	 * @return {string} - The generated HTML ID for the select input element.
	 */
	getSelectInputHTMLID(pInputHTMLID)
	{
		return `#SELECT-FOR-${pInputHTMLID}`;
	}

	/**
	 * Generates the HTML ID for a select input element in PDF display.
	 * @param {string} pInputHTMLID - The HTML ID of the input element.
	 * @return {string} - The generated HTML ID for the select input element in PDF display.
	 */
	getSelectPDFDisplayHTMLID(pInputHTMLID)
	{
		return `#DISPLAY-FOR-${pInputHTMLID}`;
	}

	/**
	 * Generates a tabular select input ID based on the provided input HTML ID and row index.
	 * @param {string} pInputHTMLID - The input HTML ID.
	 * @param {number} pRowIndex - The row index.
	 * @return {string} - The generated tabular select input ID.
	 */
	getTabularSelectInputID(pInputHTMLID, pRowIndex)
	{
		return `#SELECT-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Generates a tabular select input ID for PDF display based on the provided input HTML ID and row index.
	 * @param {string} pInputHTMLID - The input HTML ID.
	 * @param {number} pRowIndex - The row index.
	 * @return {string} - The generated tabular select input ID for PDF display.
	 */
	getTabularSelectPDFDisplayHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#DISPLAY-TABULAR-FOR-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Generates a tabular select dropdown ID based on the input HTML ID and row index.
	 * @param {string} pInputHTMLID - The HTML ID of the input.
	 * @param {number} pRowIndex - The index of the row.
	 * @return {string} - The generated tabular select dropdown ID.
	 */
	getTabularSelectDropdownID(pInputHTMLID, pRowIndex)
	{
		return `#SELECT-TABULAR-DROPDOWN-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Resolve an entity-backed option list for this input (see `_EntityOptionSource`), kicking off the
	 * shared load on first ask. Returns null while the load is in flight, when the input is not
	 * entity-backed, and when the customer has no rows - in each case the caller keeps whatever static
	 * `PictForm.SelectOptions` fallback the input carries.
	 *
	 * @param {Object} pInput - The input object.
	 * @param {Function} [fOnLoaded] - Invoked once, after an in-flight load resolves, so the caller can repaint.
	 * @return {Array<{id: any, text: string}>|null} - The option list, or null to fall back.
	 */
	resolveEntitySelectOptions(pInput, fOnLoaded)
	{
		return libEntityOptionSource.resolveOptions(this, pInput, fOnLoaded);
	}

	/**
	 * Build the selection list for a select input.
	 * @param {HTMLElement} pInputSelectElement - The select element.
	 * @param {Array} pListData - The list data.
	 * @param {Object} pInput - The input object.
	 * @param {Array<string>} pValues - The input value.
	 * @return {Array} - The filtered list data.
	 */
	rebuildSelectionList(pInputSelectElement, pListData, pInput, pValues)
	{
		// HAX
		pInputSelectElement.innerHTML = '';

		if (pInputSelectElement && pListData && Array.isArray(pListData))
		{
			if (pListData.length === 0)
			{
				// add the empty list option
				const pOption = document.createElement('option');
				pOption.value = '';
				pOption.text = 'No options available';
				pInputSelectElement.appendChild(pOption);
			}
			else
			{
				// add the placeholder option
				const pOption = document.createElement('option');
				pOption.value = '';
				pOption.text = this.pict.AppData.Interactive ? (pInput?.PictForm?.Placeholder || 'Select...') : '';
				pInputSelectElement.appendChild(pOption);

				for (let i = 0; i < pListData.length; i++)
				{
					// check if the passed value matches this option, and set the selected property
					pListData[i].selected = (pValues.find((v) => v == pListData[i].id) != null);
					// add the option to the select element
					const pOption = document.createElement('option');
					pOption.value = pListData[i].id;
					pOption.text = pListData[i].text;
					pOption.selected = pListData[i].selected;
					pInputSelectElement.appendChild(pOption);
				}
			}
		}
		return pListData;
	}

	/**
	 * Refreshes the select list for a dynamic input.
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {Array<string>|string} pValues - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {boolean} isTabular - Whether the context is tabular or not.
	 * @param {number|null} pRowIndex - The row index for tabular context (null for non-tabular context).
	 * @return {boolean|Array} - Returns false if the input element is not found, otherwise returns the filtered list data.
	 */
	refreshSelectList(pView, pGroup, pRow, pInput, pValues, pHTMLSelector, isTabular = false, pRowIndex = null)
	{
		if (typeof pValues === 'string')
		{
			pValues = pValues.split(',').filter((v) => v.trim() !== '');
		}
		const methodNameByContext = isTabular ? 'getTabularSelectDropdownID' : 'getSelectInputHTMLID';
		// Try to get the input element
		/** @type {Array<HTMLElement>|HTMLElement} */
		let tmpInputSelectElement = this.pict.ContentAssignment.getElement(this[methodNameByContext](pInput.Macro.RawHTMLID, pRowIndex));
		let tmpListData = pInput.PictForm?.SelectOptions;

		// Entity-backed vocabularies (PictForm.SelectOptionsEntity) win over the static list when the
		// customer has rows; an empty table or a failed read leaves the static SelectOptions in place.
		const tmpEntityOptions = this.resolveEntitySelectOptions(pInput, () =>
		{
			this.refreshSelectList(pView, pGroup, pRow, pInput, pValues, pHTMLSelector, isTabular, pRowIndex);
			const tmpReloadedElement = this.pict.ContentAssignment.getElement(this[methodNameByContext](pInput.Macro.RawHTMLID, pRowIndex));
			if (window['jQuery'] && tmpReloadedElement && tmpReloadedElement.length > 0)
			{
				window['jQuery'](tmpReloadedElement[0]).trigger('change.select2');
			}
		});
		if (Array.isArray(tmpEntityOptions) && tmpEntityOptions.length > 0)
		{
			tmpListData = tmpEntityOptions;
		}

		if (pInput.PictForm.SelectOptionsPickList)
		{
			if (this.pict.providers.DynamicMetaLists.listDefinitions[pInput.PictForm.SelectOptionsPickList]?.Dynamic)
			{
				return false;
			}
			if (this.pict.providers.DynamicMetaLists.hasList(pInput.PictForm.SelectOptionsPickList))
			{
				tmpListData = this.pict.providers.DynamicMetaLists.getList(pInput.PictForm.SelectOptionsPickList);
			}
		}

		// TODO: Determine later if this should ever be an array.
		if (!tmpInputSelectElement || tmpInputSelectElement.length < 1)
		{
			return false;
		}

		tmpListData = this.pict.providers.ListDistilling.filterList(pView, pInput, tmpListData);

		tmpInputSelectElement = tmpInputSelectElement[0];

		return this.rebuildSelectionList(tmpInputSelectElement, tmpListData, pInput, pValues);
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (this.pict.providers.DynamicInput)
		{
			this.pict.providers.DynamicInput.registerIgnoredEventHash('change.select2');
		}
		this._onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, false, null);
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Initializes a tabular input element.
	 * NOTE: The tabular version uses a different select element/id for the dropdown selection by design.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the row.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the initialization.
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		if (this.pict.providers.DynamicInput)
		{
			this.pict.providers.DynamicInput.registerIgnoredEventHash('change.select2');
		}
		this._onInputInitialize(pView, pGroup, null, pInput, pValue, pHTMLSelector, true, pRowIndex);
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Internal method to initialize the input element for both regular and tabular contexts.
	 * @private
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object|null} pRow - The row object (null for tabular context).
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {boolean} isTabular - Whether the context is tabular or not.
	 * @param {number|null} pRowIndex - The row index for tabular context (null for non-tabular context).
	 */
	_onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, isTabular = false, pRowIndex = null)
	{
		const methodNameByContext = isTabular ? 'getTabularSelectInputID' : 'getSelectInputHTMLID';
		// add loading skeleton while we initialize the select2 plugin and load the options list, since this can take a moment and we want to avoid the user interacting with an uninitialized select element
		this.addLoadingSkeleton(this[methodNameByContext](pInput.Macro.RawHTMLID, pRowIndex), pInput.Macro.RawHTMLID);
		// if this is a multiple select, we need to add the 'multiple' attribute to the select element and add the 'is-multiple' class to the wrapper for Bulma styling purposes
		const tmpIsMultiple = pInput.PictForm.InputType === 'Select2Multiple' || pInput.PictForm.Multiple == true;
		if (tmpIsMultiple)
		{
			this.pict.ContentAssignment.setAttribute(this[methodNameByContext](pInput.Macro.RawHTMLID, pRowIndex), 'multiple', 'multiple');
			// from the select tag, find the parent wrapper div with class '.control.select' so we can add the 'is-multiple' class to it (for Bulma styling purposes)
			const tmpSelectWrapper = document.querySelector(`${this[methodNameByContext](pInput.Macro.RawHTMLID, pRowIndex)}`)?.parentElement;
			if (tmpSelectWrapper && tmpSelectWrapper.classList.contains('select') && !tmpSelectWrapper.classList.contains('is-multiple'))
			{
				tmpSelectWrapper.classList.add('is-multiple');
			}
			window['jQuery'](this[methodNameByContext](pInput.Macro.RawHTMLID, pRowIndex)).empty(); // remove placeholder
		}
		// ensure we use the right value format for multiple vs. single select inputs
		const tmpValues = tmpIsMultiple ? (pValue && typeof(pValue) === 'string' ? pValue.split(',') : []) : pValue ? [ pValue ] : [];
		// handle the case where the options list is coming from a dynamic pick list that needs to be loaded asynchronously
		this.refreshSelectList(pView, pGroup, pRow, pInput, tmpValues, pHTMLSelector, isTabular, pRowIndex);
	}

	/**
	 * Initialize the Select2 plugin for the input element.
	 * @param {object} pInput The input object.
	 * @param {boolean} isTabular Whether the input is tabular.
	 * @param {number} [pRowIndex] The row index for tabluar rows.
	 */
	initializeSelect2(pInput, isTabular = false, pRowIndex)
	{
		if (this.pict.AppData.Interactive === false)
		{
			// if the app is not interactive, we don't need to initialize the select2 plugin
			return;
		}
		if (window['jQuery'] && window['jQuery'].fn.select2)
		{
			// tabular vs. non-tabular context
			const methodNameByContext = isTabular ? 'getTabularSelectDropdownID' : 'getSelectInputHTMLID';
			const tmpSelectElementSelector = this[methodNameByContext](pInput.Macro.RawHTMLID, pRowIndex);
			// if the select2 plugin is not already initialized, initialize it now
			if (tmpSelectElementSelector && !window['jQuery'](tmpSelectElementSelector).data('select2'))
			{
				// NOTE: Select2 Option Documentation: https://select2.org/configuration/options-api
				const tmpSelectOptions = {
					allowClear: true,
					disabled: pInput.PictForm.Disabled,
					multiple: pInput.PictForm.InputType === 'Select2Multiple' || pInput.PictForm.Multiple == true,
					placeholder: this.pict.AppData.Interactive ? (pInput?.PictForm?.Placeholder || 'Select...') : '',
					width: '100%',
					tags: pInput.PictForm.AllowCustomValues,
					dropdownCssClass: pInput.PictForm.AllowCustomValues ? 'has-custom-options' : '',
					Disabled: pInput.PictForm.Disabled || false,
					createTag: function(params)
					{
						const term = params.term.trim();
						if (term === '')
						{
							return null;
						}
						return {
							id: term,
							text: term,
							newTag: true,
						};
					},
				};
				if (pInput.PictForm.SelectOptionsPickList && this.pict.providers.DynamicMetaLists.listDefinitions[pInput.PictForm.SelectOptionsPickList]?.Dynamic)
				{
					tmpSelectOptions.ajax = {}; // necessary for custom data adapter to be used
					tmpSelectOptions.dataAdapter = $.fn.select2.amd.require('select2/data/dynamicLocalPaginationAdapter');
					// plumbing necessary context for the adapter since it doesn't have access to pict
					tmpSelectOptions.Input = pInput;
					tmpSelectOptions.DynamicMetaLists = this.pict.providers.DynamicMetaLists; //FIXME: ...
				}
				// initialize the select2 plugin on the select element
				window['jQuery'](tmpSelectElementSelector).select2(tmpSelectOptions);
			}
		}
	}

	/**
	 * Handles the marshalling of data to the form for select input types, including setting the selected options on the select element and refreshing the options list if necessary.
	 * @param {object} pView The view object.
	 * @param {object} pGroup The group object.
	 * @param {object} pInput The input object.
	 * @param {string} pHTMLSelector The HTML selector.
	 * @param {number} pRowIndex The row index for tabular context.
	 * @param {boolean} isTabular Whether the context is tabular or not.
	 * @param {HTMLSelectElement} tmpInputSelectElement The select input element to set the options and values on.
	 * @param {boolean} tmpIsMultiple Whether the select input allows multiple selections or not.
	 * @param {Array<string>} tmpValues The input values to set on the select element.
	 */
	_onDataMarshalToForm(pView, pGroup, pInput, pHTMLSelector, pRowIndex, isTabular = false, tmpInputSelectElement, tmpIsMultiple, tmpValues)
	{
		// TODO: Consolidated a lot of the excess to this method so we can abstract/remove it proplery when we migrate to initalizing select2 in onInitialize and only refreshing the options list in onDataMarshalToForm,
		// but for now we need to ensure the select2 plugin is initialized before we set the options and values because of how "AllowCustomValues" works (since it relies on the "createTag" option in select2 which needs to be set at initialization),
		// and we also need to handle the case where the options list is coming from a dynamic pick list that needs to be loaded asynchronously, so we have some extra plumbing here to handle those cases for now.
		const tmpDynamicValues = pInput.PictForm.SelectOptionsPickList && this.pict.providers.DynamicMetaLists.listDefinitions[pInput.PictForm.SelectOptionsPickList]?.Dynamic;
		let tmpChanged = false;
		for (const tmpValue of tmpValues)
		{
			let tmpValueSelected = false;

			//FIXME: this only works for initialization; it does nothing after select2 takes over
			for (let i = 0; i < tmpInputSelectElement.options.length; i++)
			{
				if (tmpInputSelectElement.options[i].value === tmpValue)
				{
					tmpInputSelectElement.options[i].selected = true;
					if (!tmpIsMultiple)
					{
						tmpInputSelectElement.selectedIndex = i;
						tmpChanged = true;
					}
					tmpValueSelected = true;
					break;
				}
			}

			if (tmpValue && !tmpValueSelected)
			{
				if (pInput.PictForm?.AllowCustomValues || tmpDynamicValues)
				{
					// if the value is not found in the select options, add it as a custom value
					const tmpOption = document.createElement('option');
					const tmpOptionInfo = pInput.PictForm.SelectOptionsPickList && this.pict.providers.DynamicMetaLists.getList(pInput.PictForm.SelectOptionsPickList, { extractRootKey: tmpValue })[0];
					tmpOption.value = tmpOptionInfo?.id ?? tmpValue;
					tmpOption.text = tmpOptionInfo?.text ?? tmpValue;
					tmpOption.selected = true;
					tmpInputSelectElement.appendChild(tmpOption);
					tmpChanged = true;
				}
				else if (!tmpIsMultiple)
				{
					tmpInputSelectElement.selectedIndex = -1;
					tmpChanged = true;
					this.pict.log.error(`The value [${tmpValue}] was not found in the select options for input [${pInput.Macro.RawHTMLID}] but was set in the hidden HTML input.`);
				}
			}
		}
		// only refresh the select list if custom values are not allowed (we don't want to lose custom values)
		if (!pInput.PictForm?.AllowCustomValues)
		{
			const tmpRow = pView.getRow(pInput.PictForm.GroupIndex, pRowIndex);
			this.refreshSelectList(pView, pGroup, tmpRow, pInput, tmpValues, pHTMLSelector, isTabular, pRowIndex);
		}
		// TODO: This needs to be moved to onInitialize and cleaned up, but for now we need to ensure the select2 plugin is initialized before we set the options and values
		// NOTE: There needs to be some adjustments to "allowCustomValues" to continue to work.
		this.initializeSelect2(pInput, isTabular, pRowIndex);
		// if we need to push change to Select2 but don't want it trigger the change event (which can cause issues with cascading selects),
		// we can trigger the 'change.select2' event which will update the Select2 UI without triggering the change event handlers
		if (tmpIsMultiple)
		{
			const tmpCurrentSelection = new Set($(tmpInputSelectElement).val() || []);
			if (tmpValues.length !== tmpCurrentSelection.size)
			{
				$(tmpInputSelectElement).val(tmpValues);
				tmpChanged = true;
			}
			else
			{
				for (const tmpValue of tmpValues)
				{
					if (!tmpCurrentSelection.has(tmpValue))
					{
						$(tmpInputSelectElement).val(tmpValues);
						tmpChanged = true;
						break;
					}
				}
			}
		}
		else
		{
			const tmpCurrentValue = $(tmpInputSelectElement).val();
			if (tmpValues.length === 0 && tmpCurrentValue != null || tmpCurrentValue != tmpValues[0])
			{
				$(tmpInputSelectElement).val(tmpValues.length > 0 ? tmpValues[0] : null);
				tmpChanged = true;
			}
		}
		if (tmpChanged)
		{
			$(tmpInputSelectElement).trigger('change.select2');
		}
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the value is successfully marshaled to the form, otherwise false.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpIsMultiple = pInput.PictForm.InputType === 'Select2Multiple' || pInput.PictForm.Multiple == true;
		const tmpValues = tmpIsMultiple ? (pValue && typeof(pValue) === 'string' ? pValue.split(',') : []) : pValue ? [ pValue ] : [];
		// When there is no live <select> to drive, render the selected option's label into
		// the static DISPLAY-FOR node instead. This covers the non-interactive PDF view AND
		// the Read (locked) theme, whose Tier-2.5 a11y treatment renders a static display node
		// rather than a `<select disabled>`. In Edit the DISPLAY-FOR id is absent, so the
		// assignContent inside handlePDFDisplay is a harmless no-op.
		const tmpInputSelectElements = this.pict.ContentAssignment.getElement(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID));
		if (this.pict.AppData.Interactive === false || !tmpInputSelectElements || tmpInputSelectElements.length < 1)
		{
			this.handlePDFDisplay(pInput, pValue, null, false, tmpValues);
			return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		}
		this._onDataMarshalToForm(pView, pGroup, pInput, pHTMLSelector, null, false, tmpInputSelectElements[0], tmpIsMultiple, tmpValues);
		this.removeLoadingSkeleton(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID), pInput.Macro.RawHTMLID);
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to a form in tabular format.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value parameter.
	 * @param {string} pHTMLSelector - The HTML selector parameter.
	 * @param {number} pRowIndex - The row index parameter.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the data marshaling.
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		const tmpIsMultiple = pInput.PictForm.InputType === 'Select2Multiple' || pInput.PictForm.Multiple == true;
		const tmpValues = tmpIsMultiple ? (pValue && typeof(pValue) === 'string' ? pValue.split(',') : []) : pValue ? [ pValue ] : [];
		// As with the standard case: when there is no live <select> (PDF view or the Read
		// theme's static display node), render the label into the DISPLAY-TABULAR-FOR node.
		const tmpInputSelectElements = this.pict.ContentAssignment.getElement(this.getTabularSelectDropdownID(pInput.Macro.RawHTMLID, pRowIndex));
		if (this.pict.AppData.Interactive === false || !tmpInputSelectElements || tmpInputSelectElements.length < 1)
		{
			this.handlePDFDisplay(pInput, pValue, pRowIndex, true, tmpValues);
			return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		}
		this._onDataMarshalToForm(pView, pGroup, pInput, pHTMLSelector, pRowIndex, true, tmpInputSelectElements[0], tmpIsMultiple, tmpValues);
		this.removeLoadingSkeleton(this.getTabularSelectDropdownID(pInput.Macro.RawHTMLID, pRowIndex), pInput.Macro.RawHTMLID);
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Handles the display of the select input value for PDF view.
	 * TODO: This will go away when we migrate the initialization to be on the onInitialize event instead of onDataMarshalToForm, but for now,
	 * abstracting the mess to make it easier to remove later.
	 *
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be displayed.
	 * @param {number} pRowIndex - The row index for tabular context.
	 * @param {boolean} isTabular - Whether the context is tabular or not.
	 * @param {Array<string>} tmpValues - The array of values to be displayed.
	 */
	handlePDFDisplay(pInput, pValue, pRowIndex, isTabular = false, tmpValues)
	{
		const methodNameByContext = isTabular ? 'getTabularSelectPDFDisplayHTMLID' : 'getSelectPDFDisplayHTMLID';
		// set the display value
		const tmpDisplayValues = [];
		let tmpDefaultData = pInput.PictForm.SelectOptions;

		// Entity-backed vocabularies label the Read / PDF nodes from the loaded rows, matching Edit. This
		// path never runs refreshSelectList, so it is usually what STARTS the load — repaint once it
		// lands, otherwise a managed-only code would be stuck showing as its raw code here.
		const tmpEntityOptions = this.resolveEntitySelectOptions(pInput, () => this.handlePDFDisplay(pInput, pValue, pRowIndex, isTabular, tmpValues));
		if (Array.isArray(tmpEntityOptions) && tmpEntityOptions.length > 0)
		{
			tmpDefaultData = tmpEntityOptions;
		}

		// When the input is backed by a dynamic picklist (SelectOptionsPickList) rather than a
		// static SelectOptions array, resolve the option set from the DynamicMetaLists provider —
		// the same source the Edit/marshal paths use. Without this the Read (locked) and PDF
		// display nodes stay blank for picklist-backed Select2 inputs, since SelectOptions is empty.
		if ((!tmpDefaultData || tmpDefaultData.length < 1) &&
			pInput.PictForm.SelectOptionsPickList &&
			this.pict.providers.DynamicMetaLists.hasList(pInput.PictForm.SelectOptionsPickList))
		{
			tmpDefaultData = this.pict.providers.DynamicMetaLists.getList(pInput.PictForm.SelectOptionsPickList);
		}

		for (let i=0; i<tmpValues.length; i++)
		{
			// find the text value for the selected value (by id)
			const tmpIndex = (tmpDefaultData && tmpDefaultData.length > 0) ?
				tmpDefaultData.findIndex((tmpSelection) => tmpSelection.id == tmpValues[i]) :
				-1;
			if (tmpIndex > -1)
			{
				tmpDisplayValues.push(tmpDefaultData[tmpIndex].text);
			}
			else if (pInput.PictForm.AllowCustomValues || pInput.PictForm.SelectOptionsEntity)
			{
				// Custom / free-entry values have no option row; show the raw value so a
				// picklist-backed or custom selection still renders in Read and PDF views. An
				// entity-backed vocabulary gets the same treatment: a stored code that has been
				// retired from the managed list still renders as its code rather than blank.
				tmpDisplayValues.push(tmpValues[i]);
			}
		}
		this.pict.ContentAssignment.assignContent(this[methodNameByContext](pInput.Macro.RawHTMLID, pRowIndex), tmpDisplayValues.join(', '));
		return;
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @return {any} - The result of the onDataRequest method.
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		const tmpInputSelectValue = this.pict.ContentAssignment.readContent(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID));
		this.pict.ContentAssignment.assignContent(pHTMLSelector, tmpInputSelectValue);
		pView.dataChanged(pInput.Hash);
		return super.onDataRequest(pView, pInput, tmpInputSelectValue, pHTMLSelector);
	}

	/**
	 * Handles the data request event for a tabular input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index.
	 * @return {any} - The result of the data request.
	 */
	onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		const tmpInputSelectValue = this.pict.ContentAssignment.readContent(this.getTabularSelectDropdownID(pInput.Macro.RawHTMLID, pRowIndex));
		this.pict.ContentAssignment.assignContent(this.getTabularSelectInputID(pInput.Macro.RawHTMLID, pRowIndex), tmpInputSelectValue);
		pView.dataChangedTabular(pInput.PictForm.GroupIndex, pInput.PictForm.InputIndex, pRowIndex);
		return super.onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex);
	}

	/**
	 * Called when the data change function is called
	 *
	 * Called when an input has this Provider hash in its 'Providers' array.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The new value of the input object
	 * @param {string} pHTMLSelector - The HTML selector for the input object
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		// Special handling for filters inputs, do not remove.
		if (pView.Hash === 'PSRSFilterProxyView')
		{
			this.pict.manifest.setValueAtAddress(pView.Bundle, pInput.PictForm.InformaryDataAddress.replace('StringArrayValue', 'Values'), pValue ? pValue.split(',') : []);
		}
		return super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}
}

module.exports = CustomInputHandler;
